import {
    type ViewStyle,
    StyleSheet,
    Platform
} from "react-native";
import {
    type SwitchTrackDirectionConstantType,
    type SwitchThumbOffsetConstantType,
    type SwitchStateStyleConstantType,
    type SwitchMeasuresConstantType,
    type SwitchColorsConstantType,
    type SwitchTypeConstantType,
    type SwitchDynamicStyleType,
    type SwitchStateStyles,
    type SwitchMeasures,
    type SwitchColors,
    type SwitchTypes
} from "./type";
import {
    getTypeColors
} from "../../helpers/theme/typeColors";
import type {
    Mutable
} from "../../types";
import {
    SELECTION_CONTROL_ELEVATION_LEVEL
} from "../selectionControl/stylesheet";
import {
    getFocusRingStyle,
    getStateColorKey
} from "../../helpers/theme/state";
import {
    getElevationStyle
} from "../../helpers/theme/elevation";
import {
    getDirectionSign
} from "../../helpers/direction";
import {
    getMotionTransition
} from "../../helpers/motion";
import {
    getOpaqueColor,
    webStyle
} from "../../utils";

export const getSwitchMeasures = ({
    iconSizes,
    radiuses,
    spaces,
    size
}: SwitchMeasuresConstantType): SwitchMeasures => {
    const inset = spaces.spacingXxs;

    const trackHeight = size === "small" ? iconSizes.medium : iconSizes.large;

    const trackWidth = trackHeight * 2 - inset * 2;

    const thumbSize = trackHeight - inset * 2;

    return {
        travel: trackWidth - thumbSize - inset * 2,
        containerHeight: iconSizes.large,
        borderRadius: radiuses.full,
        trackHeight,
        trackWidth,
        thumbSize,
        inset
    };
};

export const getSwitchThumbOffset = ({
    currentMeasures,
    direction
}: SwitchThumbOffsetConstantType): number => {
    return getDirectionSign({
        direction
    }) * currentMeasures.travel;
};

export const getSwitchTrackDirection = ({
    direction
}: SwitchTrackDirectionConstantType): ViewStyle => {
    if(Platform.OS === "web") {
        return {};
    }

    return {
        direction
    };
};

export const getSwitchType = ({
    colors,
    type
}: SwitchTypeConstantType): SwitchTypes => {
    const filledColors = getTypeColors({
        variant: "filled",
        colors,
        type
    });

    return {
        selectedColor: filledColors.containerColor
    };
};

export const getSwitchColors = ({
    currentType,
    isDisabled,
    isChecked,
    colors
}: SwitchColorsConstantType): SwitchColors => {
    if(isDisabled) {
        const disabledFilledColors = getTypeColors({
            variant: "filled",
            type: "neutral",
            isDisabled,
            colors
        });

        if(isChecked) {
            return {
                spinnerColor: disabledFilledColors.containerColor,
                overlayColor: disabledFilledColors.overlayColor,
                trackColor: getOpaqueColor({
                    surface: colors.system.default.background,
                    color: disabledFilledColors.iconColor
                }),
                thumbColor: colors.content.container.default
            };
        }

        return {
            overlayColor: disabledFilledColors.overlayColor,
            trackColor: getOpaqueColor({
                color: disabledFilledColors.containerColor,
                surface: colors.system.default.background
            }),
            thumbColor: getOpaqueColor({
                surface: colors.system.default.background,
                color: disabledFilledColors.iconColor
            }),
            spinnerColor: disabledFilledColors.iconColor
        };
    }

    if(isChecked) {
        return {
            thumbColor: colors.content.container.default,
            spinnerColor: currentType.selectedColor,
            trackColor: currentType.selectedColor,
            overlayColor: "default"
        };
    }

    return {
        thumbColor: colors.content.container.default,
        spinnerColor: colors.content.icon.mid,
        trackColor: colors.content.icon.low,
        overlayColor: "default"
    };
};

export const getSwitchStateStyle = ({
    isReduceMotionEnabled,
    isFocusVisible,
    durations,
    borders,
    colors,
    type
}: SwitchStateStyleConstantType): SwitchStateStyles => {
    const styles = {
        track: {
            ...getFocusRingStyle({
                colorKey: getStateColorKey({
                    type
                }),
                isFocusVisible,
                borders,
                colors
            })
        } as Mutable<ViewStyle>
    };

    Object.assign(styles.track, webStyle(getMotionTransition({
        isReduceMotionEnabled,
        properties: [
            "background-color"
        ],
        intent: "hover",
        durations
    })));

    return styles;
};

const stylesheet = StyleSheet.create({
    thumb: {
        justifyContent: "center",
        alignItems: "center"
    },
    trackContainer: {
        justifyContent: "center"
    },
    track: {
        position: "absolute",
        flexDirection: "row",
        alignItems: "center"
    }
});

export const useStyles = ({
    currentMeasures,
    currentColors,
    shadowAlpha,
    elevations,
    isDisabled,
    direction,
    colors
}: SwitchDynamicStyleType) => {
    const styles = {
        track: {
            top: (currentMeasures.containerHeight - currentMeasures.trackHeight) / 2,
            borderRadius: currentMeasures.borderRadius,
            backgroundColor: currentColors.trackColor,
            paddingHorizontal: currentMeasures.inset,
            height: currentMeasures.trackHeight,
            width: currentMeasures.trackWidth
        } as Mutable<ViewStyle>,
        thumb: {
            borderRadius: currentMeasures.borderRadius,
            backgroundColor: currentColors.thumbColor,
            height: currentMeasures.thumbSize,
            width: currentMeasures.thumbSize
        } as Mutable<ViewStyle>,
        trackContainer: {
            height: currentMeasures.containerHeight,
            width: currentMeasures.trackWidth
        } as Mutable<ViewStyle>
    };

    Object.assign(styles.track, getSwitchTrackDirection({
        direction
    }));

    if(!isDisabled) {
        Object.assign(styles.thumb, getElevationStyle({
            level: SELECTION_CONTROL_ELEVATION_LEVEL,
            shadowColor: colors.system.shadow,
            shadowAlpha,
            elevations
        }));
    }

    return styles;
};
export default stylesheet;
